import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AccommodationType, formatPlaceName } from '@aviv_price-pages/brand';
import { Box, useBreakpointValue } from '@gemini/core';
import { ArrowDownFilledCircleIcon, ArrowRightIcon, ArrowUpFilledCircleIcon, BuildingIcon, HouseIcon, } from '@gemini/icons';
import { LineChart } from '@gemini/patterns-linechart';
import { Card, Divider, Tabs, TextButton } from '@gemini/ui';
import { PriceEvolutionLevel, } from '@/entities/types/Indice';
import { useAccommodationType } from '@/hooks/useAccommodationType/useAccommodationType';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
const getRange = (values, step) => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const rangeMin = Math.floor(min / step) * step;
    const rangeMax = Math.ceil(max / step) * step;
    const range = [];
    for (let i = rangeMin; i <= rangeMax; i += step) {
        range.push(i);
    }
    return range;
};
const PriceEvolutionChart = ({ indices, pricePageContext }) => {
    const { t } = useShellTranslation();
    const { accommodationType, setAccommodationType } = useAccommodationType(indices);
    const chartData = [];
    const xTicks = [];
    const yTicks = [];
    const values = indices[accommodationType]?.values;
    if (values && values.length > 0) {
        const yTicksArray = getRange(values, 20);
        yTicks.push(...yTicksArray);
    }
    indices[accommodationType]?.values.forEach((value, index) => {
        chartData.push({
            group: new Intl.DateTimeFormat('default', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(new Date(indices[accommodationType]?.dates[index])) || '',
            pois: { indices: Number(value.toFixed(1)) },
        });
    });
    indices[accommodationType]?.dates.forEach((date, index) => {
        const formattedDate = new Intl.DateTimeFormat('default').format(new Date(indices[accommodationType]?.dates[index])) || '';
        const year = new Date(formattedDate).getFullYear();
        const tick = `01/01/${year}`;
        if (!xTicks.includes(tick)) {
            xTicks.push(tick);
        }
    });
    const displayArrow = useBreakpointValue({
        base: false,
        xs: true,
    });
    if (!indices?.apartment && !indices?.house) {
        return null;
    }
    const formatedPlaceName = formatPlaceName(indices.indicesPlace || pricePageContext.place, {
        isShortName: true,
    });
    const getPlaceName = () => {
        return t(`place_name.${pricePageContext.place.type}`) ===
            `place_name.${pricePageContext.place.type}`
            ? formatedPlaceName
            : t(`place_name.${pricePageContext.place.type}`);
    };
    return (_jsxs(Box, { display: "flex", flexDirection: "column", paddingTop: "spacing.24", gap: "spacing.24", alignItems: "center", children: [_jsx(Box, { as: "h2", typography: "typography.headline.20.bold", width: "100%", paddingTop: "spacing.16", children: t('price_evolution_chart_block.info_view.title', {
                    replace: {
                        place: getPlaceName(),
                    },
                    unsafe_escapeValue: false,
                }) }), _jsx(Divider, { marginTop: "-spacing.16" }), indices?.apartment && indices?.house ? (_jsx(Box, { width: "100%", children: _jsx(Tabs, { value: accommodationType, onChange: (value) => {
                        setAccommodationType(value);
                    }, items: [
                        {
                            id: AccommodationType.APARTMENT,
                            icon: () => _jsx(BuildingIcon, { marginRight: "spacing.8" }),
                            iconActive: () => _jsx(BuildingIcon, { marginRight: "spacing.8" }),
                            label: t('price_block.info_view.apartment'),
                            content: '',
                        },
                        {
                            id: AccommodationType.HOUSE,
                            icon: () => _jsx(HouseIcon, { marginRight: "spacing.8" }),
                            iconActive: () => _jsx(HouseIcon, { marginRight: "spacing.8" }),
                            label: t('price_block.info_view.house'),
                            content: '',
                        },
                    ] }) })) : (_jsx(Box, { typography: "typography.body.14.regular", width: "100%", paddingHorizontal: "spacing.16", children: t(`price_evolution_chart_block.info_view.subtitle_${accommodationType}s`) })), _jsx(Card, { borderRadius: "8", children: _jsx(Box, { display: "flex", paddingVertical: "spacing.16", children: indices[accommodationType]?.priceEvolution?.map((priceEvolution, index) => {
                        return (_jsxs(Box, { display: "flex", flexDirection: "column", alignItems: "center", borderLeftStyle: index !== 0 ? 'solid' : 'unset', borderLeftColor: "color.border.surface.default", borderLeftWidth: "borderWidth.1", width: "100%", children: [_jsxs(Box, { as: "span", textAlign: "center", typography: "typography.body.12.regular", children: [priceEvolution?.timePeriodValue, ' ', t(`price_evolution_chart_block.info_view.${priceEvolution?.timePeriodUnit}`, {
                                            count: priceEvolution?.timePeriodValue,
                                        })] }), priceEvolution?.priceEvolutionLevel ===
                                    PriceEvolutionLevel.EQUAL ? (_jsx(Box, { display: "flex", alignItems: "center", height: "100%", typography: "typography.body.14.bold", children: "0%" })) : (_jsxs(Box, { as: "span", unsafe_style: { textWrap: 'nowrap' }, children: [displayArrow &&
                                            (priceEvolution?.priceEvolutionLevel ===
                                                PriceEvolutionLevel.HIGH ? (_jsx(ArrowUpFilledCircleIcon, { color: "color.content.status.success.default", size: "sizing.16", marginRight: "spacing.4" })) : (_jsx(ArrowDownFilledCircleIcon, { color: "color.content.status.error.default", size: "sizing.16", marginRight: "spacing.4" }))), _jsx(Box, { as: "span", typography: "typography.body.14.bold", unsafe_style: { textWrap: 'nowrap' }, children: _jsxs("span", { translate: "no", children: [priceEvolution?.priceEvolutionLevel ===
                                                        PriceEvolutionLevel.HIGH
                                                        ? '+'
                                                        : '-', ' ', priceEvolution?.percentagePriceEvolution, "%"] }) })] }))] }, `${index}-${priceEvolution?.timePeriodValue}`));
                    }) }) }), indices?.apartment && indices?.house && (_jsx(LineChart, { "aria-description": t('price_evolution_chart_block.info_view.chart_aria_description'), "aria-label": t('price_evolution_chart_block.info_view.chart_aria_label'), axisProps: {
                    ticks: {
                        category: xTicks,
                        number: yTicks,
                    },
                    domain: ['dataMin - 10', 'dataMax + 10'],
                }, showLegend: false, data: chartData, dataTemplate: {
                    pois: {
                        indices: {
                            label: 'Indices',
                        },
                    },
                    formatPoiValue: (value) => `${value}`,
                    formatPoiCategory: (value) => Intl.DateTimeFormat('fr-FR', { year: 'numeric' }).format(new Date(value)),
                }, emptyState: {
                    description: 'Please try again later.',
                    title: 'No data available',
                } })), _jsx(Box, { textAlign: "left", width: "100%", children: _jsx(TextButton, { onPress: () => {
                        const element = document.getElementById('price-table');
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                        }
                    }, icon: ArrowRightIcon, iconPosition: "end", size: "14", children: t('price_table_block.info_view.button_redirection') }) })] }));
};
export default PriceEvolutionChart;
